import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { ElementRef, QueryList } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { UsersListService } from "src/app/services/users/users-list.service";
import { QueryParamsForGetUsers } from "src/app/services/api/db3d-api-backend-client.service";
import { UserDetails } from "src/app/data-models/manage-users/user-details.model";
import { UsersPagination } from "src/app/data-models/manage-users/users-pagination.model";
import { NavigationService } from "src/app/services/NavigationService.service";
import { environment } from "src/environments/environment";
import { queryParamsKey, sortArrowActiveNameClass, sortDirection, TSortDirection } from "src/app/services/users/users-list.constants";
import { IUsersPaginationApiResponse } from "src/app/factories/users-pagination-factory";
import { IUserDetailsApiResponse } from "src/app/factories/user-details-factory";

export abstract class UsersListBase {
  public environment = environment;
  protected readonly usersQuerySearchDelay = 250;
  protected readonly initPageNumber = "1";

  public users: UserDetails[];
  public pagination: UsersPagination;
  public searchInputValue = new BehaviorSubject<string>("");
  public usersFetchingInProgress: boolean;
  public serverError: boolean;
  public noSearchingResults: boolean;
  private lastSearchQuery = "";
  private currentOrdering: { [key: string]: TSortDirection } = {};
  private searchSubscription: Subscription;
  public static sortActiveClassName = sortArrowActiveNameClass;
  protected abstract arrows: QueryList<ElementRef<HTMLElement>>;

  protected constructor(
    protected usersListService: UsersListService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected navigationService: NavigationService,
  ) {}

  protected initialize(queryParamsFromUrl?: QueryParamsForGetUsers): void {
    this.currentOrdering = this.deserializeQueryParamsToCurrentOrdering(this.route.snapshot.queryParamMap.get(queryParamsKey.sort));
    this.getUsersListFromApi(queryParamsFromUrl);
    if (queryParamsFromUrl?.search) this.searchInputValue.next(queryParamsFromUrl.search);
    this.initSearchHandling();
  }

  protected initSearchHandling(): void {
    this.searchSubscription = this.searchInputValue.pipe(debounceTime(this.usersQuerySearchDelay)).subscribe((inputValue: string) => {
      this.router.navigate([], {
        queryParams: {
          search: inputValue === "" ? null : inputValue,
        },
        relativeTo: this.route,
        queryParamsHandling: "merge",
        skipLocationChange: false,
      });

      if (this.lastSearchQuery !== inputValue) {
        this.lastSearchQuery = inputValue;
        const queryParams: QueryParamsForGetUsers = {
          page: this.initPageNumber,
          search: inputValue,
          ordering: this.route.snapshot.queryParamMap.get(queryParamsKey.sort),
          userRole: this.route.snapshot.queryParamMap.get(queryParamsKey.userRole),
        };
        this.getUsersListFromApi(queryParams);
      }
    });
  }

  protected deserializeQueryParamsToCurrentOrdering(queryParams: string): { [key: string]: TSortDirection } {
    const ordering: { [key: string]: TSortDirection } = {};
    if (queryParams) {
      const params = queryParams.split(",");
      params.map((param: string) => {
        if (param[0] === sortDirection.descPrefix)
          ordering[param.substring(1)] = sortDirection.desc;
        else
          ordering[param] = sortDirection.asc;
      });
    }
    return ordering;
  }

  protected serializeCurrentOrderingToQueryParams(currentOrdering: { [key: string]: TSortDirection }): string {
    const orderingInQueryParams = [] as string[];
    for (const [key, value] of Object.entries(currentOrdering)) {
      if (value === sortDirection.asc)
        orderingInQueryParams.push(key);
      else if (value === sortDirection.desc)
        orderingInQueryParams.push(`${sortDirection.descPrefix}${key}`);
    }
    return orderingInQueryParams.toString();
  }

  protected updateCurrentOrdering(direction: TSortDirection, sortByParam: string): void {
    if (this.currentOrdering[sortByParam] === direction)
      delete this.currentOrdering[sortByParam];
    else
      this.currentOrdering[sortByParam] = direction;
  }

  public sortColumn(direction: TSortDirection, sortBy: string): void {
    this.updateDisplayedUsersList(direction, sortBy);
  }

  protected updateDisplayedUsersList(direction: TSortDirection, sortByParam: string): void {
    this.updateCurrentOrdering(direction, sortByParam);
    const newOrderingQueryParams = this.serializeCurrentOrderingToQueryParams(this.currentOrdering);

    this.router.navigate([], {
      queryParams: {
        ordering: newOrderingQueryParams,
      },
      relativeTo: this.route,
      queryParamsHandling: "merge",
      skipLocationChange: false,
    });

    this.getSortedUsersList(newOrderingQueryParams);
  }

  protected getSortedUsersList(newOrderingQuery: string): void {
    const queryParams: QueryParamsForGetUsers = {
      page: this.initPageNumber,
      search: this.route.snapshot.queryParamMap.get(queryParamsKey.search),
      ordering: newOrderingQuery,
      userRole: this.route.snapshot.queryParamMap.get(queryParamsKey.userRole),
    };
    this.getUsersListFromApi(queryParams);
  }

  public changePage(pageNumber: number): void {
    if (typeof pageNumber !== "number" || pageNumber <= 0) {
      console.warn("Invalid page number: must be a number greater than 0.");
      return;
    }

    const queryParams: QueryParamsForGetUsers = {
      page: pageNumber.toString(),
      search: this.route.snapshot.queryParamMap.get(queryParamsKey.search),
      ordering: this.route.snapshot.queryParamMap.get(queryParamsKey.sort),
      userRole: this.route.snapshot.queryParamMap.get(queryParamsKey.userRole),
    };
    this.getUsersListFromApi(queryParams);
  }

  protected addPaginationParamsToUrl(paginationPage: number): void {
    this.router.navigate([], {
      queryParams: {
        page: paginationPage,
      },
      relativeTo: this.route,
      queryParamsHandling: "merge",
      skipLocationChange: false,
    });
  }

  protected getUsersListFromApi(queryParamsFromUrl?: QueryParamsForGetUsers): void {
    this.usersFetchingInProgress = true;
    this.serverError = false;
    this.usersListService.getUsersList(this.environment.db3dBackendDomain, queryParamsFromUrl).subscribe({
      next: (usersFromApi: {items: IUserDetailsApiResponse[], pagination: IUsersPaginationApiResponse}) => {
        this.users = this.usersListService.mapUsers(usersFromApi.items || []);
        this.pagination = this.usersListService.mapPagination(usersFromApi.pagination || {} );
        this.addPaginationParamsToUrl(usersFromApi.pagination?.page || 1);
        this.users.length === 0 ? this.noSearchingResults = true : this.noSearchingResults = false;
        this.usersFetchingInProgress = false;
        this.serverError = false;
      },
      error: (err) => {
        if (err?.status === 403) this.router.navigate([this.navigationService.pageNotFound]);
        this.usersFetchingInProgress = false;
        this.serverError = true;
        this.noSearchingResults = false;
      }
    });
  }

  public addActiveStyleForSortingArrows(orderingQueryParams: string): void {
    if (!orderingQueryParams) return;
    const params = orderingQueryParams.split(",");
    params.map((param: string) => {
      if (param[0] === sortDirection.descPrefix)
        this.findSortingArrow(sortDirection.desc, param.substring(1))?.nativeElement.classList.add((UsersListBase).sortActiveClassName);
      else
        this.findSortingArrow(sortDirection.asc, param)?.nativeElement.classList.add((UsersListBase).sortActiveClassName);
    });
  }

  public findSortingArrow(direction: string, sortBy: string): ElementRef<HTMLElement> | undefined {
    if (!this.arrows) return undefined;
    return this.arrows.find((arrow: ElementRef<HTMLElement>) => arrow.nativeElement.id === `${direction}-${sortBy}`);
  }

  ngOnDestroy(): void {
    if (this.searchSubscription)
      this.searchSubscription.unsubscribe();
  }
}
